/**
 * Pure mapping/derivation helpers for staff-onboarding (no React, no I/O).
 *
 * `buildStaffCreatePayload` turns the wizard's masked/string form values
 * into the exact `StaffCreateMerchantPayload` the backend expects:
 * digits-only documents, numeric money/time, omitted-when-empty optionals,
 * and `operatingHours` rows with ISO weekdays. Kept pure so it is unit
 * tested directly (see `payload.test.ts`).
 */
import { onlyDigits } from '../../lib/cpf';
import type { PixKeyType } from '../../lib/pix';
import { strings } from './strings';
import {
  type BusinessType,
  type DispatchMode,
  type IsoWeekday,
  type MerchantAddressPayload,
  type OperatingHoursPayload,
  type StaffCreateMerchantPayload,
} from './types';

/** One editable operating-hours row as held in the form. */
export interface OperatingHoursRow {
  dayOfWeek: IsoWeekday;
  /** `HH:mm`. */
  open: string;
  /** `HH:mm`. */
  close: string;
  closed: boolean;
}

/** Raw wizard form values (mostly masked strings — the source for mapping). */
export interface NewMerchantFormValues {
  // Step 1 — establishment
  tradeName: string;
  legalName: string;
  cnpj: string;
  businessType: BusinessType;
  categoryId: string;
  categoryName: string;
  description: string;
  phone: string;
  // Step 2 — responsible
  ownerFullName: string;
  ownerCpf: string;
  ownerPhone: string;
  ownerEmail: string;
  tempPassword: string;
  tempPasswordRepeat: string;
  // Step 3 — address
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  /** Manual map pin (RF-004/RF-005); both null until the operator drops the marker. Source of truth. */
  latitude: number | null;
  longitude: number | null;
  // Step 4 — media
  logoUrl: string;
  /** Owner document photos (FAV-142) — with either present the owner is created documentStatus=PENDING. */
  ownerFacePhotoUrl: string;
  ownerIdentityPhotoUrl: string;
  // Step 5 — operations
  operatingHours: OperatingHoursRow[];
  minimumOrderAmount: string;
  estimatedPrepMinutes: string;
  dispatchMode: DispatchMode;
  autoAcceptOrders: boolean;
  // Step 4 — PIX payout key (FAV-172). `pixKeyType` is `''` when none chosen.
  pixKeyType: '' | PixKeyType;
  pixKey: string;
}

const trimmed = (v: string): string => v.trim();

const optionalString = (v: string | undefined): string | undefined => {
  const t = (v ?? '').trim();
  return t.length > 0 ? t : undefined;
};

const optionalNumber = (raw: string): number | undefined => {
  const t = raw.trim();
  if (t.length === 0) return undefined;
  const parsed = Number(t.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const optionalInt = (raw: string): number | undefined => {
  const t = raw.trim();
  if (t.length === 0) return undefined;
  const parsed = Number.parseInt(t, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

function buildAddress(v: NewMerchantFormValues): MerchantAddressPayload {
  const complement = optionalString(v.complement);
  // RF-004: send the manual pin only as a complete pair (both-or-neither — backend @AssertTrue).
  const hasPin =
    typeof v.latitude === 'number' &&
    Number.isFinite(v.latitude) &&
    typeof v.longitude === 'number' &&
    Number.isFinite(v.longitude);
  return {
    zipCode: onlyDigits(v.zipCode),
    street: trimmed(v.street),
    number: trimmed(v.number),
    neighborhood: trimmed(v.neighborhood),
    city: trimmed(v.city),
    state: trimmed(v.state).toUpperCase(),
    ...(complement !== undefined ? { complement } : {}),
    ...(hasPin ? { latitude: v.latitude as number, longitude: v.longitude as number } : {}),
  };
}

/** Maps form rows → `OperatingHoursPayload[]` (all 7 days, closed flag preserved). */
export function buildOperatingHours(rows: OperatingHoursRow[]): OperatingHoursPayload[] {
  return rows.map((r) => ({
    dayOfWeek: r.dayOfWeek,
    openTime: r.open,
    closeTime: r.close,
    isClosed: r.closed,
  }));
}

/**
 * Builds the `POST /api/v1/staff/merchants` body. Strips masks, omits empty
 * optionals (required by `exactOptionalPropertyTypes`), always includes
 * `tempPassword` (FAV-112 hybrid). Owner document photos go in the FAV-142
 * fields — with either present the backend creates the owner `documentStatus=PENDING`.
 */
export function buildStaffCreatePayload(v: NewMerchantFormValues): StaffCreateMerchantPayload {
  const legalName = optionalString(v.legalName);
  const description = optionalString(v.description);
  const phone = onlyDigits(v.phone);
  const categoryId = optionalString(v.categoryId);
  const ownerFacePhotoUrl = optionalString(v.ownerFacePhotoUrl);
  const ownerIdentityPhotoUrl = optionalString(v.ownerIdentityPhotoUrl);
  const minimumOrderAmount = optionalNumber(v.minimumOrderAmount);
  const estimatedPrepMinutes = optionalInt(v.estimatedPrepMinutes);
  // PIX is optional (FAV-172): send (key, type) only when a key was entered.
  const pixKey = optionalString(v.pixKey);
  const pixKeyType: PixKeyType | undefined =
    pixKey !== undefined && v.pixKeyType !== '' ? v.pixKeyType : undefined;

  return {
    tradeName: trimmed(v.tradeName),
    cnpj: onlyDigits(v.cnpj),
    businessType: v.businessType,
    category: trimmed(v.categoryName),
    ownerFullName: trimmed(v.ownerFullName),
    ownerCpf: onlyDigits(v.ownerCpf),
    ownerPhone: onlyDigits(v.ownerPhone),
    ownerEmail: trimmed(v.ownerEmail),
    tempPassword: v.tempPassword,
    address: buildAddress(v),
    logoUrl: trimmed(v.logoUrl),
    operatingHours: buildOperatingHours(v.operatingHours),
    dispatchMode: v.dispatchMode,
    autoAcceptOrders: v.autoAcceptOrders,
    ...(legalName !== undefined ? { legalName } : {}),
    ...(categoryId !== undefined ? { categoryId } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(phone.length > 0 ? { phone } : {}),
    ...(ownerFacePhotoUrl !== undefined ? { ownerFacePhotoUrl } : {}),
    ...(ownerIdentityPhotoUrl !== undefined ? { ownerIdentityPhotoUrl } : {}),
    ...(minimumOrderAmount !== undefined ? { minimumOrderAmount } : {}),
    ...(estimatedPrepMinutes !== undefined ? { estimatedPrepMinutes } : {}),
    ...(pixKey !== undefined && pixKeyType !== undefined ? { pixKey, pixKeyType } : {}),
  };
}

/* ── Dashboard metrics (híbrido) ───────────────────────────────────── */

export interface OnboardingMetricsInput {
  /** Count of APPROVED merchants (null while loading/unavailable). */
  activeStores: number | null;
  /** Count of active categories. */
  activeCategories: number | null;
}

export interface MetricCard {
  key: string;
  label: string;
  value: string;
  hint?: string;
}

/**
 * Builds the 4 dashboard metric cards (mockup). "Aprovados/mês" and
 * "Pedidos total" are stubbed "—" — they need `GET /staff/me/stats`
 * (TODO[FAV-127]); the other two are computed from available data.
 */
export function computeOnboardingMetrics(input: OnboardingMetricsInput): MetricCard[] {
  const fmt = (n: number | null): string =>
    n === null ? strings.metrics.unavailable : String(n);
  return [
    { key: 'activeStores', label: strings.metrics.activeStores, value: fmt(input.activeStores) },
    {
      key: 'approvedThisMonth',
      label: strings.metrics.approvedThisMonth,
      value: strings.metrics.unavailable,
      hint: strings.metrics.unavailableHint,
    },
    {
      key: 'totalOrders',
      label: strings.metrics.totalOrders,
      value: strings.metrics.unavailable,
      hint: strings.metrics.unavailableHint,
    },
    {
      key: 'activeCategories',
      label: strings.metrics.activeCategories,
      value: fmt(input.activeCategories),
    },
  ];
}

/** Default operating hours: Mon–Sat open 08:00–18:00, Sunday closed. */
export function defaultOperatingHours(): OperatingHoursRow[] {
  return ([1, 2, 3, 4, 5, 6, 7] as IsoWeekday[]).map((dayOfWeek) => ({
    dayOfWeek,
    open: '08:00',
    close: '18:00',
    closed: dayOfWeek === 7,
  }));
}
